import { invokeWithRetry } from './common/invokeWithRetry';

/**
 * Note de contrat : `invokeWithRetry` renseigne désormais `error` en même temps
 * que `authExpired`. Les court-circuits `if (authExpired) return <succès>`
 * fabriquaient des réponses vides ou des `{ success: true }` mensongers pour
 * une session perdue ; ils sont supprimés et le cas retombe sur `if (error)`.
 */

// Feature flags for bookings management edge function
const FEATURE_FLAGS = {
  GET_BOOKINGS_WITH_FILTERS: true,
  GET_BOOKING_BY_CLASS_AND_USER: true,
  DELETE_BOOKING: true,
  GET_BOOKING_HISTORY: true,
  VALIDATE_BOOKING_INTERVAL: true,
};

export interface BookingData {
  id: string;
  userId: string;
  className: string;
  coach: string;
  date: string;
  time: string;
  duration: string;
  status: string;
  classId: string;
  capacity?: number;
}

export interface BookingFilters {
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/**
 * Get bookings with filters
 */
export async function getBookingsWithFilters(
  gymId: string,
  filters: BookingFilters
) {
  if (!FEATURE_FLAGS.GET_BOOKINGS_WITH_FILTERS) {
    return { data: null, error: new Error('Feature flag disabled'), totalCount: 0 };
  }

  const { data, error } = await invokeWithRetry("bookings-management", {
    action: "get_bookings_with_filters",
    gym_id: gymId,
    ...filters,
  });

  if (error) {
    console.error("[bookingsManagementWrapper] get_bookings_with_filters error:", error);
    return { data: null, error, totalCount: 0 };
  }

  return { 
    data: data.bookings || [], 
    error: null, 
    totalCount: data.total_count || 0 
  };
}

/**
 * Get a specific booking by class, user, and date
 */
export async function getBookingByClassAndUser(
  gymId: string,
  classId: string,
  userId: string,
  classDate: string
) {
  if (!FEATURE_FLAGS.GET_BOOKING_BY_CLASS_AND_USER) {
    return { data: null, error: new Error('Feature flag disabled') };
  }

  const { data, error } = await invokeWithRetry("bookings-management", {
    action: "get_booking_by_class_and_user",
    gym_id: gymId,
    class_id: classId,
    user_id: userId,
    class_date: classDate,
  });

  if (error) {
    console.error("[bookingsManagementWrapper] get_booking_by_class_and_user error:", error);
    return { data: null, error };
  }

  return { data: data.booking || null, error: null };
}

/**
 * Delete a booking
 */
export async function deleteBooking(
  gymId: string,
  bookingId: string
) {
  if (!FEATURE_FLAGS.DELETE_BOOKING) {
    return { data: null, error: new Error('Feature flag disabled') };
  }

  const { data, error } = await invokeWithRetry("bookings-management", {
    action: "delete_booking",
    gym_id: gymId,
    booking_id: bookingId,
  });

  if (error) {
    console.error("[bookingsManagementWrapper] delete_booking error:", error);
    return { data: null, error };
  }

  return { data: data || { success: true }, error: null };
}

/**
 * Get booking history for the current user
 */
export async function getBookingHistory(gymId: string) {
  if (!FEATURE_FLAGS.GET_BOOKING_HISTORY) {
    return { data: null, error: new Error('Feature flag disabled') };
  }

  const { data, error } = await invokeWithRetry("bookings-management", {
    action: "get_booking_history",
    gym_id: gymId,
  });

  if (error) {
    console.error("[bookingsManagementWrapper] get_booking_history error:", error);
    return { data: null, error };
  }

  return { data: data.history || [], error: null };
}

// ==================== BOOKING VALIDATION ====================

export interface BookingValidationResult {
  isValid: boolean;
  message?: string;
  conflictingBookingDateTime?: string;
}

/**
 * Validate booking interval (48h rule) via edge function
 * Ensures isolation per gym - bookings in other gyms don't affect validation
 */
export async function validateBookingInterval(
  gymId: string,
  userId: string,
  classDate: string,
  classTime: string
): Promise<{ data: BookingValidationResult | null; error: any }> {
  if (!FEATURE_FLAGS.VALIDATE_BOOKING_INTERVAL) {
    return { data: { isValid: true }, error: null };
  }

  const { data, error } = await invokeWithRetry("bookings-management", {
    action: "validate_booking_interval",
    gym_id: gymId,
    userId,
    classDate,
    classTime,
  });

  if (error) {
    console.error("[bookingsManagementWrapper] validate_booking_interval error:", error);
    // Fail open ASSUMÉ : la règle des 48 h est aussi appliquée côté serveur au
    // moment de la réservation. Bloquer ici sur une panne réseau interdirait
    // des réservations légitimes ; `error` est renvoyé pour que l'appelant
    // puisse tracer.
    return { data: { isValid: true }, error };
  }

  return { data, error: null };
}

/**
 * Get the count of upcoming bookings for the current user in a specific gym.
 * Enforces multi-tenant isolation server-side (no direct DB call from frontend).
 */
export async function getUserUpcomingBookingsCount(
  gymId: string,
  userId: string
): Promise<{ data: { upcomingCount: number } | null; error: any }> {
  const { data, error } = await invokeWithRetry("bookings-management", {
    action: "get_user_upcoming_count",
    gym_id: gymId,
    user_id: userId,
  });

  if (error) {
    console.error("[bookingsManagementWrapper] get_user_upcoming_count error:", error);
    return { data: null, error };
  }

  return { data: { upcomingCount: data?.upcomingCount ?? 0 }, error: null };
}
